import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { ExpandLess, ExpandMore, Place } from '@mui/icons-material'
import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material'

import React, { useState } from 'react'

const canteenLocationSelectionUserDataFragment = graphql(`
  fragment CanteenLocationSelection_UserDataFragment on User {
    accountLocation {
      id
    }
    account {
      locationsPaged(filters: { isCCActive: true }) {
        rows {
          label
          id
        }
      }
    }
  }
`)

type Props = {
  userData: FragmentType<typeof canteenLocationSelectionUserDataFragment>
  onChange?: (accountLocationId: number) => void
  disabled?: boolean
  selectedLocationId?: string
}

const LocationSelectionGraphql: React.FC<Props> = ({ userData, onChange = () => {}, disabled }) => {
  const { account, accountLocation } = useFragment(
    canteenLocationSelectionUserDataFragment,
    userData,
  )
  const locations = account?.locationsPaged.rows || []
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLocationSelection = (value: string) => {
    if (value === String(accountLocation?.id)) return
    onChange(Number(value))
    handleClose()
  }

  const open = Boolean(anchorEl)

  const selectedLocationOption = locations.find((location) => {
    const idToUse = accountLocation?.id
    return location?.id === idToUse
  })

  // Simplified display-only mode when disabled
  if (disabled) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Place color="action" />
        <Typography variant="body2">{selectedLocationOption?.label}</Typography>
      </Stack>
    )
  }

  return (
    <>
      <Button
        onClick={handleClick}
        aria-haspopup="true"
        id="basic-button"
        variant="outlined"
        size="medium"
        fullWidth
        sx={{
          borderColor: 'divider',
          minHeight: '40px',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
        startIcon={<Place color="primary" />}
        endIcon={<>{open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}</>}>
        <Typography noWrap>{selectedLocationOption?.label}</Typography>
      </Button>
      <Menu
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          maxHeight: '400px',
        }}
        anchorEl={anchorEl}
        id="basic-menu"
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}>
        <Stack>
          {locations.map((location) => (
            <MenuItem
              onClick={() => handleLocationSelection(location?.id || '')}
              key={location?.id}>
              <Typography paddingX={2}>{location?.label}</Typography>
            </MenuItem>
          ))}
        </Stack>
      </Menu>
    </>
  )
}

export default LocationSelectionGraphql
